import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { DataService } from 'src/services/data-service';
import {
  IEmbassyInfo,
  EmbassyWeeklySchedule,
  EmbassyDaySchedule,
  TimeWindow,
  DayOfWeek,
  EmbassyWeeklySchedulePayload,
  EmbassyDaySchedulePayload,
  TimeWindowPayload
} from '.';

interface IAddEmbassyInfoProps {
  onSuccess: () => void;
  onClose: () => void;
  embassyInfo?: IEmbassyInfo | null;
}

const daysOfWeek: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const defaultTimeWindow = (): TimeWindow => ({
  start: '09:00',
  end: '17:00'
});

const defaultDaySchedule = (): EmbassyDaySchedule => ({
  open: false,
  windows: []
});

const defaultWeeklySchedule = (): EmbassyWeeklySchedule => ({
  monday: defaultDaySchedule(),
  tuesday: defaultDaySchedule(),
  wednesday: defaultDaySchedule(),
  thursday: defaultDaySchedule(),
  friday: defaultDaySchedule(),
  saturday: defaultDaySchedule(),
  sunday: defaultDaySchedule()
});

const AddEmbassyInfo: React.FC<IAddEmbassyInfoProps> = ({ onSuccess, onClose, embassyInfo }) => {
  const dispatch = useAppDispatch();
  const isEditMode = !!embassyInfo;

  const [weeklySchedule, setWeeklySchedule] = useState<EmbassyWeeklySchedule>(defaultWeeklySchedule());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (embassyInfo?.weeklySchedule) {
      setWeeklySchedule(embassyInfo.weeklySchedule);
    }
  }, [embassyInfo]);

  const handleDayToggle = (day: DayOfWeek) => {
    const dayKey = day.toLowerCase() as Lowercase<DayOfWeek>;
    setWeeklySchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        open: !prev[dayKey].open,
        windows: !prev[dayKey].open && prev[dayKey].windows.length === 0
          ? [defaultTimeWindow()]
          : prev[dayKey].windows
      }
    }));
  };

  const handleAddTimeWindow = (day: DayOfWeek) => {
    const dayKey = day.toLowerCase() as Lowercase<DayOfWeek>;
    setWeeklySchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        windows: [...prev[dayKey].windows, defaultTimeWindow()]
      }
    }));
  };

  const handleRemoveTimeWindow = (day: DayOfWeek, index: number) => {
    const dayKey = day.toLowerCase() as Lowercase<DayOfWeek>;
    setWeeklySchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        windows: prev[dayKey].windows.filter((_, i) => i !== index)
      }
    }));
  };

  const handleTimeWindowChange = (
    day: DayOfWeek,
    index: number,
    field: 'start' | 'end',
    value: string
  ) => {
    const dayKey = day.toLowerCase() as Lowercase<DayOfWeek>;
    setWeeklySchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        windows: prev[dayKey].windows.map((window, i) =>
          i === index ? { ...window, [field]: value } : window
        )
      }
    }));
  };

  const validateSchedule = (): boolean => {
    for (const day of daysOfWeek) {
      const dayKey = day.toLowerCase() as Lowercase<DayOfWeek>;
      const daySchedule = weeklySchedule[dayKey];

      if (daySchedule.open) {
        if (daySchedule.windows.length === 0) {
          dispatch(showAlert({
            message: `${day} is marked as open but has no time windows`,
            type: 'error'
          }));
          return false;
        }

        for (let i = 0; i < daySchedule.windows.length; i++) {
          const window = daySchedule.windows[i];
          if (!window.start || !window.end) {
            dispatch(showAlert({
              message: `${day} has invalid time window at position ${i + 1}`,
              type: 'error'
            }));
            return false;
          }

          if (window.start >= window.end) {
            dispatch(showAlert({
              message: `${day} time window ${i + 1}: Start time must be before end time`,
              type: 'error'
            }));
            return false;
          }
        }
      }
    }
    return true;
  };

  // Convert lowercase schedule to PascalCase for API
  const convertToPascalCase = (schedule: EmbassyWeeklySchedule): EmbassyWeeklySchedulePayload => {
    const convertDay = (daySchedule: EmbassyDaySchedule): EmbassyDaySchedulePayload => ({
      Open: daySchedule.open,
      Windows: daySchedule.windows.map((window): TimeWindowPayload => ({
        Start: window.start,
        End: window.end
      }))
    });

    return {
      Monday: convertDay(schedule.monday),
      Tuesday: convertDay(schedule.tuesday),
      Wednesday: convertDay(schedule.wednesday),
      Thursday: convertDay(schedule.thursday),
      Friday: convertDay(schedule.friday),
      Saturday: convertDay(schedule.saturday),
      Sunday: convertDay(schedule.sunday)
    };
  };

  const handleSubmit = useCallback(async () => {
    if (!validateSchedule()) {
      return;
    }

    setSubmitting(true);

    try {
      const pascalCaseSchedule = convertToPascalCase(weeklySchedule);

      const payload = {
        WeeklySchedule: pascalCaseSchedule
      };

      const endpoint = isEditMode
        ? 'api/EmbassyInfo/update'
        : 'api/EmbassyInfo/create';

      const requestData = isEditMode && embassyInfo
        ? { Id: embassyInfo.id, ...payload }
        : payload;

      const response = await DataService.post(endpoint, requestData);

      if (response.ok) {
        dispatch(showAlert({
          message: `Embassy schedule ${isEditMode ? 'updated' : 'created'} successfully!`,
          type: 'success'
        }));
        onSuccess();
      } else {
        const errorData = await response.json();
        dispatch(showAlert({
          message: errorData?.message || `Error ${isEditMode ? 'updating' : 'creating'} embassy schedule`,
          type: 'error'
        }));
      }
    } catch (error) {
      dispatch(showAlert({
        message: `Error ${isEditMode ? 'updating' : 'creating'} embassy schedule`,
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  }, [weeklySchedule, isEditMode, embassyInfo, dispatch, onSuccess]);

  const renderDaySchedule = (day: DayOfWeek) => {
    const dayKey = day.toLowerCase() as Lowercase<DayOfWeek>;
    const daySchedule = weeklySchedule[dayKey];

    return (
      <Accordion key={day} defaultExpanded={daySchedule.open}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={daySchedule.open}
                  onChange={() => handleDayToggle(day)}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label={day}
              sx={{ flex: 1 }}
            />
            {daySchedule.open && (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                {daySchedule.windows.length} time window(s)
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!daySchedule.open ? (
            <Typography variant="body2" color="text.secondary">
              Closed
            </Typography>
          ) : (
            <Box>
              {daySchedule.windows.map((window, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        type="time"
                        label="Start Time"
                        value={window.start}
                        onChange={(e) =>
                          handleTimeWindowChange(day, index, 'start', e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        type="time"
                        label="End Time"
                        value={window.end}
                        onChange={(e) =>
                          handleTimeWindowChange(day, index, 'end', e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveTimeWindow(day, index)}
                        disabled={daySchedule.windows.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  {index < daySchedule.windows.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddTimeWindow(day)}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                Add Time Window
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="h5">
          {isEditMode ? 'Update Embassy Schedule' : 'Create Embassy Schedule'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Weekly Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure the embassy opening hours for each day of the week
          </Typography>

          {daysOfWeek.map(day => renderDaySchedule(day))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : isEditMode ? 'Update Schedule' : 'Create Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEmbassyInfo;
